<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, type Component } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Sparkles, X } from 'lucide-vue-next'
import IconLightning from '@/assets/svg/iconLightning.svg?component'
import IconCalendar from '@/assets/svg/iconCalendar.svg?component'
import IconNotes from '@/assets/svg/iconNotes.svg?component'
import FullCalendarTS from '@/components/molecules/calendar/FullCalendarTS.vue'
import AppContainerNotes from '@/components/molecules/notes/AppContainerNotes.vue'
import { useIaCardsVisibility } from '@/composables/useIaCardsVisibility'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { useChatStore } from '@/components/whatsapp/stores/useChatStore'
import AppButton from '@/components/atoms/buttons/AppButton.vue'

type PanelType = 'calendar' | 'notes' | null

const activePanel = ref<PanelType>(null)
const route = useRoute()
const { t } = useI18n()
const workspace = useWorkspaceStore()
const chatStore = useChatStore()
const { getCampaignById } = useCampaignService()
const { isIaCardsVisible, setIaCardsVisibility } = useIaCardsVisibility()
const isSyncingIaPermission = ref(false)
const isMobileMenuOpen = ref(false)
const mobilePosition = ref({ x: 0, y: 0 })
const isDraggingMobileButton = ref(false)
const mobileDragMoved = ref(false)
const suppressMobileClick = ref(false)
const mobileDragOffset = ref({ x: 0, y: 0 })
const mobilePointerStart = ref({ x: 0, y: 0 })

const isExpanded = computed(() => activePanel.value !== null)
const canToggleIaCards = computed(
  () => route.name === 'campaign-detail' || route.name === 'WhatsApp',
)

const ACTION_BAR_RAIL_WIDTH = 64
const MOBILE_BUTTON_SIZE = 56
const MOBILE_BUTTON_MARGIN = 8

const actionBarDesktopWidth = computed(() =>
  isExpanded.value ? 'clamp(22rem, 40vw, 34rem)' : `${ACTION_BAR_RAIL_WIDTH}px`,
)

const mobileMenuStyle = computed(() => {
  const menuWidth = Math.min(352, window.innerWidth - 16)
  const menuHeight = Math.min(window.innerHeight * 0.72, 620)
  const shortcutCount = canToggleIaCards.value ? 3 : 2
  const shortcutHeight = shortcutCount * 48 + (shortcutCount - 1) * 8
  const left = Math.max(
    8,
    Math.min(mobilePosition.value.x + 56 - menuWidth, window.innerWidth - menuWidth - 8),
  )
  const opensDown = mobilePosition.value.y < window.innerHeight / 2
  const top = opensDown
    ? Math.min(
        mobilePosition.value.y + 64 + shortcutHeight + 8,
        window.innerHeight - menuHeight - 8,
      )
    : Math.max(8, mobilePosition.value.y - menuHeight - shortcutHeight - 8)

  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${menuWidth}px`,
    maxHeight: `${menuHeight}px`,
  }
})

const mobileShortcutStyle = computed(() => {
  const shortcutCount = canToggleIaCards.value ? 3 : 2
  const shortcutHeight = shortcutCount * 48 + (shortcutCount - 1) * 8
  const opensDown = mobilePosition.value.y < window.innerHeight / 2

  return {
    left: `${mobilePosition.value.x}px`,
    top: opensDown
      ? `${mobilePosition.value.y + 64}px`
      : `${Math.max(8, mobilePosition.value.y - shortcutHeight - 8)}px`,
  }
})

const clampMobilePosition = (x: number, y: number) => {
  const margin = MOBILE_BUTTON_MARGIN
  mobilePosition.value = {
    x: Math.max(margin, Math.min(x, window.innerWidth - MOBILE_BUTTON_SIZE - margin)),
    y: Math.max(margin, Math.min(y, window.innerHeight - MOBILE_BUTTON_SIZE - margin)),
  }
}

const snapMobileButtonToSide = () => {
  const rightPosition = window.innerWidth - MOBILE_BUTTON_SIZE - MOBILE_BUTTON_MARGIN
  const middle = window.innerWidth / 2

  mobilePosition.value.x =
    mobilePosition.value.x + MOBILE_BUTTON_SIZE / 2 < middle ? MOBILE_BUTTON_MARGIN : rightPosition
}

const handleMobileResize = () => {
  clampMobilePosition(mobilePosition.value.x, mobilePosition.value.y)
  snapMobileButtonToSide()
}

const stopMobileDrag = () => {
  if (!isDraggingMobileButton.value) return

  isDraggingMobileButton.value = false
  snapMobileButtonToSide()
  suppressMobileClick.value = mobileDragMoved.value
  mobileDragMoved.value = false
  window.removeEventListener('pointermove', moveMobileButton)
  window.removeEventListener('pointerup', stopMobileDrag)
}

const moveMobileButton = (event: PointerEvent) => {
  if (!isDraggingMobileButton.value) return

  if (
    Math.abs(event.clientX - mobilePointerStart.value.x) > 5 ||
    Math.abs(event.clientY - mobilePointerStart.value.y) > 5
  ) {
    mobileDragMoved.value = true
  }

  clampMobilePosition(
    event.clientX - mobileDragOffset.value.x,
    event.clientY - mobileDragOffset.value.y,
  )
}

const startMobileDrag = (event: PointerEvent) => {
  mobileDragMoved.value = false
  mobilePointerStart.value = { x: event.clientX, y: event.clientY }
  mobileDragOffset.value = {
    x: event.clientX - mobilePosition.value.x,
    y: event.clientY - mobilePosition.value.y,
  }
  isDraggingMobileButton.value = true
  window.addEventListener('pointermove', moveMobileButton)
  window.addEventListener('pointerup', stopMobileDrag)
}

const toggleMobileMenu = () => {
  if (suppressMobileClick.value) {
    suppressMobileClick.value = false
    return
  }

  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const togglePanel = (panel: Exclude<PanelType, null>) => {
  // Las tarjetas de IA viven fuera de este componente, pero siguen siendo
  // una opción del mismo Action Bar: al abrir otra opción se cierran.
  setIaCardsVisibility(false)
  activePanel.value = activePanel.value === panel ? null : panel
}

// Los diálogos y desplegables de Calendario/Notas se teletransportan al body,
// por lo que no son descendientes del Action Bar en el DOM aunque, desde la
// perspectiva del usuario, sigan perteneciendo a su panel.
const actionBarInteractiveSelector = [
  '[data-action-bar]',
  '.p-dialog-mask',
  '.p-datepicker',
  '.p-datepicker-panel',
  '.p-select-overlay',
  '.p-popover',
  '.p-menu',
  '.fc-popover',
].join(', ')

const handleOutsidePanelPointerDown = (event: PointerEvent) => {
  if (!activePanel.value) return

  const target = event.target
  if (target instanceof Element && target.closest(actionBarInteractiveSelector)) return

  activePanel.value = null
}

// También cubre los casos en los que la vista activa abre IA desde otro lugar
// (por ejemplo, al iniciar una llamada).
watch(isIaCardsVisible, (visible) => {
  if (visible) activePanel.value = null
})

if (typeof window !== 'undefined') {
  mobilePosition.value = {
    x: window.innerWidth - MOBILE_BUTTON_SIZE - MOBILE_BUTTON_MARGIN,
    y: window.innerHeight - MOBILE_BUTTON_SIZE - 24,
  }
  window.addEventListener('resize', handleMobileResize)
}

onMounted(() => {
  document.addEventListener('pointerdown', handleOutsidePanelPointerDown)
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', handleOutsidePanelPointerDown)
  window.removeEventListener('resize', handleMobileResize)
  window.removeEventListener('pointermove', moveMobileButton)
  window.removeEventListener('pointerup', stopMobileDrag)
})

const activeIaCampaignId = computed(() => {
  if (route.name === 'campaign-detail') {
    return String(route.params.id || workspace.callCampaignId || workspace.selectedCampaignId || '')
  }

  if (route.name === 'WhatsApp') {
    return chatStore.selectedChat?.campaign_id || workspace.selectedCampaignId || ''
  }

  return workspace.callCampaignId || workspace.selectedCampaignId || ''
})

const syncIaPermission = async () => {
  const campaignId = activeIaCampaignId.value
  if (!campaignId) return

  isSyncingIaPermission.value = true
  try {
    const campaign = await getCampaignById(campaignId)
    const useAi = workspace.getCampaignUseAi(campaign)

    workspace.setCampaignUseIa(campaignId, useAi)
    workspace.setUseIa(useAi)
  } finally {
    isSyncingIaPermission.value = false
  }
}

const handleToggleIaCards = async () => {
  if (isIaCardsVisible.value) {
    setIaCardsVisibility(false)
    return
  }

  activePanel.value = null
  if (!isIaCardsVisible.value) {
    await syncIaPermission()
  }

  setIaCardsVisibility(true)
}

type ButtonAction = {
  element: 'calendar' | 'notes'
  icon: Component
  tooltipKey: string
}

const buttonActions: ButtonAction[] = [
  {
    element: 'calendar',
    icon: IconCalendar,
    tooltipKey: 'action_bar.calendar',
  },
  {
    element: 'notes',
    icon: IconNotes,
    tooltipKey: 'action_bar.notes',
  },
]
</script>

<template>
  <!-- Botón flotante y accesos rápidos para móvil -->
  <div class="lg:hidden">
    <Teleport to="body">
      <button
        type="button"
        data-action-bar
        class="fixed z-[110] flex h-14 w-14 touch-none select-none items-center justify-center rounded-full border border-white/20 bg-[var(--p-primary-color)] backdrop-blur-xl text-white shadow-md shadow-black/20 transition-transform active:scale-95 lg:hidden"
        :style="{ left: `${mobilePosition.x}px`, top: `${mobilePosition.y}px` }"
        :aria-expanded="isMobileMenuOpen"
        aria-label="Abrir barra de acciones"
        @pointerdown.prevent="startMobileDrag"
        @click="toggleMobileMenu"
      >
        <IconLightning class="h-7 w-7" />
        <span
          v-if="isMobileMenuOpen"
          class="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-[var(--p-primary-color)] shadow"
        >
          ×
        </span>
      </button>
    </Teleport>

    <div
      v-if="isMobileMenuOpen"
      data-action-bar
      class="fixed z-[105] flex flex-col gap-2"
      :style="mobileShortcutStyle"
    >
      <button
        v-for="action in buttonActions"
        :key="action.element"
        type="button"
        class="flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/90 text-[var(--p-primary-color)] shadow-xl backdrop-blur-xl transition-transform active:scale-90 dark:border-[var(--p-primary-color)]/30 dark:bg-[var(--p-surface-900)]/95"
        :class="activePanel === action.element ? 'ring-2 ring-[var(--p-primary-color)]/50' : ''"
        :aria-label="t(action.tooltipKey)"
        @click="togglePanel(action.element)"
      >
        <component :is="action.icon" class="h-5 w-5" />
      </button>

      <button
        v-if="canToggleIaCards"
        type="button"
        class="flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/90 text-[var(--p-primary-color)] shadow-xl backdrop-blur-xl transition-transform active:scale-90 dark:border-[var(--p-primary-color)]/30 dark:bg-[var(--p-surface-900)]/95"
        :class="isIaCardsVisible ? 'ring-2 ring-[var(--p-primary-color)]/50' : ''"
        :aria-label="t('action_bar.ai_assistant')"
        :disabled="isSyncingIaPermission"
        @click="handleToggleIaCards"
      >
        <Sparkles class="h-5 w-5" />
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="isMobileMenuOpen && activePanel"
        data-action-bar
        class="fixed !z-[105] flex flex-col overflow-hidden rounded-2xl border border-white/70 bg-white/95 p-3 shadow-2xl !backdrop-blur-2xl lg:hidden dark:border-[var(--p-primary-color)]/30 dark:bg-transparent"
        :style="mobileMenuStyle"
      >
        <div
          class="mb-2 flex items-center justify-between border-b border-[var(--p-surface-200)] pb-2 dark:border-[var(--p-primary-color)]/30"
        >
          <span class="font-semibold text-[var(--p-primary-color)]">
            {{ activePanel === 'calendar' ? t('action_bar.calendar') : t('action_bar.notes') }}
          </span>
          <AppButton
            text
            type="button"
            class="flex h-10 w-10! items-center justify-center rounded-full text-lg text-[var(--p-surface-500)] hover:bg-[var(--p-surface-0)]"
            aria-label="Cerrar panel"
            @click="activePanel = null"
          >
            <template #icon-start>
              <X :size="16" class=""></X>
            </template>
          </AppButton>
        </div>
        <div
          class="min-h-0 flex-1 overflow-y-auto rounded-xl border border-[var(--p-surface-200)] p-2 dark:border-[var(--p-primary-color)]/30"
        >
          <FullCalendarTS v-if="activePanel === 'calendar'" compact />
          <AppContainerNotes v-else model-value embedded title="Notas" />
        </div>
      </div>
    </Teleport>

    <!--
    <Transition name="action-bar-content">
      <div
        v-if="isMobileMenuOpen"
        class="fixed z-[105] flex flex-col overflow-hidden rounded-2xl border border-white/70 bg-white/90 p-3 shadow-2xl backdrop-blur-xl dark:border-[var(--p-primary-color)]/30 dark:bg-[var(--p-surface-950)]/95"
        :style="mobileMenuStyle"
      >
        <div class="mb-2 flex items-center justify-between border-b border-[var(--p-surface-200)] pb-2 dark:border-[var(--p-surface-700)]">
          <span class="font-semibold text-[var(--p-primary-color)]">Action Bar</span>
          <button
            type="button"
            class="flex h-8 w-8 items-center justify-center rounded-full text-lg text-[var(--p-surface-500)] hover:bg-[var(--p-primary-50)]"
            aria-label="Cerrar barra de acciones"
            @click="isMobileMenuOpen = false"
          >
            ×
          </button>
        </div>

        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="action in buttonActions"
            :key="action.element"
            type="button"
            class="flex min-h-16 flex-col items-center justify-center gap-1 rounded-xl bg-[var(--p-primary-50)] p-2 text-xs font-medium text-[var(--p-primary-color)] transition hover:bg-[var(--p-primary-color)]/20"
            :class="activePanel === action.element ? 'ring-2 ring-[var(--p-primary-color)]/40' : ''"
            @click="togglePanel(action.element)"
          >
            <component :is="action.icon" class="h-5 w-5" />
            {{ t(action.tooltipKey) }}
          </button>

          <button
            v-if="canToggleIaCards"
            type="button"
            class="flex min-h-16 flex-col items-center justify-center gap-1 rounded-xl bg-[var(--p-primary-50)] p-2 text-xs font-medium text-[var(--p-primary-color)] transition hover:bg-[var(--p-primary-color)]/20"
            :class="isIaCardsVisible ? 'ring-2 ring-[var(--p-primary-color)]/40' : ''"
            :disabled="isSyncingIaPermission"
            @click="handleToggleIaCards"
          >
            <Sparkles class="h-5 w-5" />
            {{ t('action_bar.ai_assistant') }}
          </button>
        </div>

        <div v-if="activePanel" class="mt-3 min-h-0 flex-1 overflow-y-auto rounded-xl border border-[var(--p-surface-200)] p-2 dark:border-[var(--p-surface-700)]">
          <FullCalendarTS v-if="activePanel === 'calendar'" compact />
          <AppContainerNotes v-else model-value embedded title="Notas" />
        </div>
      </div>
    </Transition>
    -->
  </div>

  <!-- Barra lateral para escritorio -->
  <div
    data-action-bar
    :class="[
      'app-card-glass hidden backdrop-blur-xl absolute left-0 top-0 z-40 overflow-hidden rounded-md shadow-sm transition-[width,height] duration-300 ease-in-out lg:left-auto lg:right-0 lg:block',
      isExpanded
        ? 'h-[calc(100dvh-56px-0.75rem)] w-full max-w-[100vw] lg:h-full lg:w-[var(--action-bar-expanded-width)] lg:max-w-[calc(100vw-5rem)]'
        : 'h-14 w-full lg:h-full lg:w-16',
    ]"
    :style="{
      '--action-bar-expanded-width': actionBarDesktopWidth,
    }"
  >
    <div class="relative h-full min-h-0 w-full">
      <!-- Rail fijo, no salta -->
      <div
        class="absolute left-0 top-0 z-10 flex h-14 w-full flex-row items-center justify-between px-3 lg:left-auto lg:right-0 lg:h-full lg:w-16 lg:flex-col lg:justify-start lg:border-b-0 lg:border-l lg:border-l-gray-100 lg:px-0 lg:py-4 lg:dark:border-l-neutral-800"
      >
        <div
          class="flex shrink-0 flex-row items-center justify-center gap-2 whitespace-nowrap text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)] lg:flex-col lg:gap-0 lg:whitespace-normal"
        >
          <IconLightning class="h-6 w-6 shrink-0 lg:h-7 lg:w-7" />

          <div class="montserrat text-center text-[11px] font-semibold lg:text-xs">Action Bar</div>
        </div>

        <div class="flex shrink-0 flex-row gap-2 lg:mt-4 lg:flex-col lg:gap-0">
          <div
            v-for="(action, index) in buttonActions"
            :key="index"
            class="flex items-center justify-center lg:py-2"
          >
            <button
              v-tooltip.left="t(action.tooltipKey)"
              type="button"
              class="flex h-8 w-8 shrink-0 shadow-md cursor-pointer items-center justify-center rounded-full bg-white/30 dark:ring-[var(--p-primary-color)]/40 dark:bg-[var(--p-primary-600)]/20 p-2 transition-colors duration-200 hover:bg-[var(--p-primary-color)]/30 lg:h-9 lg:w-9"
              :class="
                activePanel === action.element
                  ? '!bg-[var(--p-primary-color)]/40 dark:!bg-[var(--p-primary-300)]/40'
                  : ''
              "
              :aria-label="t(action.tooltipKey)"
              @click="togglePanel(action.element)"
            >
              <component :is="action.icon" class="h-4 w-4 shrink-0 text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]" />
            </button>
          </div>

          <div v-if="canToggleIaCards" class="flex items-center justify-center lg:py-2">
            <button
              v-tooltip.left="t('action_bar.ai_assistant')"
              type="button"
              class="flex h-8 w-8 shrink-0 shadow-md cursor-pointer items-center justify-center rounded-full bg-white/30 dark:ring-[var(--p-primary-color)]/40 dark:bg-[var(--p-primary-600)]/20 p-2 transition-colors duration-200 hover:bg-[var(--p-primary-color)]/30 lg:h-9 lg:w-9"
              :class="
                isIaCardsVisible
                  ? '!bg-[var(--p-primary-color)]/40 dark:!bg-[var(--p-primary-300)]/40'
                  : ''
              "
              :aria-label="t('action_bar.ai_assistant')"
              :disabled="isSyncingIaPermission"
              @click="handleToggleIaCards"
            >
              <Sparkles class="h-4 w-4 shrink-0 text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]" />
            </button>
          </div>
        </div>
      </div>

      <!-- Panel expandido -->
      <div
        v-if="isExpanded"
        class="dark:bg-[var(--p-primary-950)]/90 h-full min-h-0 w-full min-w-0 overflow-hidden p-2 pt-[66px] lg:pt-3 lg:pr-[76px]"
      >
        <Transition name="action-bar-content" mode="out-in">
          <div
            v-if="activePanel === 'calendar'"
            key="calendar"
            class="action-calendar h-full min-h-0 min-w-0 overflow-y-auto overflow-x-hidden rounded-xl px-3 text-sm text-gray-500 dark:text-gray-400"
          >
            <FullCalendarTS compact />
          </div>

          <div
            v-else-if="activePanel === 'notes'"
            key="notes"
            class="h-full min-h-0 min-w-0 overflow-y-auto overflow-x-hidden rounded-xl text-sm text-gray-500 dark:text-gray-400"
          >
            <AppContainerNotes model-value embedded title="Notas" />
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<style scoped>
.action-bar-content-enter-active,
.action-bar-content-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.action-bar-content-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

.action-bar-content-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.card-z-index {
  z-index: 9999;
}
</style>
